import { Component, inject, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FormGroup } from '@angular/forms';

import { InsuranceItemService } from '../../../../services/insurance-item.service';
import { EstimationTunnelService } from '../../../../services/estimation-tunnel.service';
import { TransportConfigurationService } from '../../../../services/transport-configuration.service';
import { CurrentDataService } from '../../../../services/current-data-service';
import { BaryModalService } from '../../../../services/bary-modal.service';
import { BaryFormFactoryService } from '../../../../services/bary-form-factory.service';

import { TransportItem } from '../../../../models/transport-item.model';
import { Transport } from '../../../../models/transport.model';
import { InsuranceItem } from '../../../../models/insurance-item.model';
import { Transporter } from '../../../../models/transporter.model';
import { ITransportConfiguration } from '../../../../models/transport-configuration.model';
import { PricingOption } from '../../../../models/pricing-option.model';
import { TransportFormGroup } from '../../../../models/forms/TransportFormGroup';
import { TransportStep } from '../../../../models/enums/transport-step.enum';
import { TRANSPORT_ITEM_ESTIMATE_STEP } from '../../../../models/enums/transport-item-estimate-step.enum';

@Component({
  selector: 'jhi-estimation-items-step',
  templateUrl: './estimation-items-step.component.html',
})
export class EstimationItemsStepComponent implements OnInit {
  readonly #baryFormFactoryService = inject(BaryFormFactoryService);
  readonly #insuranceItemsService = inject(InsuranceItemService);
  readonly #estimationValidatorService = inject(EstimationTunnelService);
  readonly #currentDataService = inject(CurrentDataService);
  readonly #modalService = inject(BaryModalService);
  readonly #translateService = inject(TranslateService);
  readonly #transportConfigurationService = inject(TransportConfigurationService);

  @Input() transport!: Transport;
  @Input() transportForm!: TransportFormGroup;
  @Input() insuranceIncluded?: boolean | undefined;
  @Input() optionInsurance?: PricingOption | undefined;

  public transporter!: Transporter;
  public transportConfiguration?: ITransportConfiguration;
  public isEuroPalletsNumberVisible = true;
  public insuranceItems: InsuranceItem[] = [];
  public insuranceItemsOption: { label: string; value: string }[] = [];

  public ngOnInit(): void {
    this.#currentDataService.getCurrentTransporter().subscribe(transporter => (this.transporter = transporter));
    this.transportForm.controls.offerId?.valueChanges.subscribe({ next: () => this.findInsuranceItems() });
    this.findInsuranceItems();
    this.#transportConfigurationService.getParams(this.transport.offerId!, TransportStep.ITEMS).subscribe(config => (this.transportConfiguration = config));
    this.#estimationValidatorService.scrollToTop();

    this.transportForm.controls.itemsNature.valueChanges.subscribe(insuranceItemLabel => {
      const insuranceItem = this.insuranceItems.find(i => i.itemsNature === insuranceItemLabel);
      if (insuranceItem) {
        this.transportForm.controls.insuranceItem.setValue(insuranceItem);
      } else {
        console.error('NO INSURANCE ITEM EXIST WITH SAME NAME OF ITEM NATURE.');
      }
    });

    this.isEuroPalletsNumberVisible = !!this.transportForm.controls.euroPallet.value;
  }

  private findInsuranceItems(): void {
    this.#insuranceItemsService.findAllByOfferId(this.transport.offerId!).subscribe(insuranceItemsResponse => {
      if (insuranceItemsResponse?.body) {
        this.insuranceItems = insuranceItemsResponse.body;
        this.insuranceItemsOption = this.insuranceItems
          .sort((it1, it2) => it1.position! - it2.position!)
          .map(
            it =>
              ({
                label: it.itemsNature,
                value: it.itemsNature,
              } as { label: string; value: string })
          );
      }
    });
  }

  public checkEuroPallets(): void {
    this.isEuroPalletsNumberVisible = !this.isEuroPalletsNumberVisible;
    this.transportForm.controls.euroPallet.setValue(this.isEuroPalletsNumberVisible ? 0 : null);
  }

  public initTransportItem(index?: number): void {
    const transportItemFormValue: any = index || index === 0 ? this.transportForm.controls.items[index].getAngularForm().getRawValue() : {};
    this.#modalService
      .openModalEstimate(this.transportForm, this.currentStep, new TransportItem(transportItemFormValue))
      .action()
      .subscribe(res => {
        if (res.submit) {
          if (index || index === 0) {
            // MAJ du transportItem
            this.transportForm.controls.items[index].updateForm((res.value as FormGroup).getRawValue());
          } else {
            //Ajout du transportItem
            this.transportForm.addTransportItem(
              new TransportItem((res.value as FormGroup).getRawValue()),
              this.#baryFormFactoryService,
              this.transportForm.controls.merchandiseType.value
            );
          }
        }
      });
  }

  private get currentStep(): TRANSPORT_ITEM_ESTIMATE_STEP {
    if (this.transportForm.controls.isControlledTemperature.value || this.transportForm.controls.isHazardousMaterial.value) {
      return TRANSPORT_ITEM_ESTIMATE_STEP.SPECIAL;
    }
    return this.transportForm.controls.items.length > 0 ? TRANSPORT_ITEM_ESTIMATE_STEP.ITEM : TRANSPORT_ITEM_ESTIMATE_STEP.SETTING;
  }

  public removeTransportItem(index: number): void {
    const messageWarning = this.#translateService.instant('estimation-items.removeItemConfirmMessage');
    this.#modalService
      .openConfirm(messageWarning)
      .action()
      .subscribe(res => {
        if (res.submit) {
          this.transportForm.removeTransportItem(index);
        }
      });
  }

  public getValueForTransportItem(transportItemForm: FormGroup): any {
    return transportItemForm.getRawValue();
  }
}
